import { useCallback, useEffect, useMemo, useState } from "react";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { useStores } from "../../../stores/setup/use-store";
import { useTranslation } from "react-i18next";

export const useAlertPhoneVerification = () => {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const toast = useToast();
	const { t } = useTranslation("translation", {
		keyPrefix: "admin.myData",
	});

	const {
		dataStores: { userConfigStore },
	} = useStores();

	const [phoneData, setPhoneData] = useState<{
		phone?: string;
		code?: string;
	}>({});

	useEffect(() => {
		if (userConfigStore.userConfig) {
			setPhoneData({
				code: userConfigStore.userConfig.phone_code || "55",
				phone: userConfigStore.userConfig.phone_number,
			});
		}
	}, [userConfigStore.userConfig]);

	const isPhoneAlreadyVerified = useMemo(() => {
		if (!phoneData.code || !phoneData.phone) {
			return false;
		}

		return (
			userConfigStore.userConfig.phone_verified &&
			phoneData.code === userConfigStore.userConfig.phone_code &&
			phoneData.phone === userConfigStore.userConfig.phone_number
		);
	}, [
		phoneData.code,
		phoneData.phone,
		userConfigStore.userConfig.phone_code,
		userConfigStore.userConfig.phone_number,
		userConfigStore.userConfig.phone_verified,
	]);

	const handlePhoneUpdateSuccess = useCallback(
		async (code?: string, phone?: string) => {
			if (!code || !phone) return;

			await userConfigStore.updateUserConfig({
				name: userConfigStore.userConfig.name,
				lastName: userConfigStore.userConfig.last_name,
				email: userConfigStore.userConfig.email,
				areaId: userConfigStore.userConfig.area_id,
				positionId: userConfigStore.userConfig.position_id,
				middleName: userConfigStore.userConfig.middle_name,
				phoneCode: code,
				phoneNumber: phone,
			});

			userConfigStore.setPhoneVerified(true);
			onClose();

			toast({
				title: t("phoneSuccessfullyUpdated"),
				status: "success",
				position: "bottom-right",
				isClosable: true,
			});
		},
		[userConfigStore, onClose, t, toast]
	);

	return {
		isOpen,
		onOpen,
		onClose,
		phoneData,
		handlePhoneUpdateSuccess,
		isPhoneAlreadyVerified,
		t,
	};
};
